// react
import React from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// relative
import { useUpdateBuildingMutation } from '../../../api/recalc/endpoints/buildingApiSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';

import { DateTime } from 'luxon';

/**
 * CO2CoefficientDialog (JavaScript)
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - building: Building object containing UserInput.CO2Coefficients
 *  - selectedMeter: billing meter object (expects BillingMeterType)
 */
export default function CO2CoefficientDialog({
  open,
  onClose,
  building,
  selectedMeter,
  defaultCoefficient,
}) {
  const { t } = useTranslation();
  const [entries, setEntries] = React.useState([]);
  const [draft, setDraft] = React.useState({ value: '', start: '', end: '' });
  const [error, setError] = React.useState('');
  const dispatch = useDispatch();
  const [updateBuilding, { isLoading: isUpdating }] =
    useUpdateBuildingMutation();

  const typeKey = React.useMemo(() => {
    const t = selectedMeter?.BillingMeterType;
    if (t === 'HeatMeter') return 'Heat';
    if (t === 'GasMeter') return 'Gas';
    if (t === 'ElectricityMeter') return 'Electricity';
    return null;
  }, [selectedMeter]);

  React.useEffect(() => {
    if (!open || !typeKey) {
      setEntries([]);
      return;
    }
    const arr =
      building?.UserInput?.CO2Coefficients?.[typeKey] &&
      Array.isArray(building.UserInput.CO2Coefficients[typeKey])
        ? [...building.UserInput.CO2Coefficients[typeKey]]
        : [];
    const normalized = arr
      .map((e) => ({
        timestamp_start: e.timestamp_start,
        timestamp_end: e.timestamp_end,
        value: Number(e.value),
      }))
      .sort(
        (a, b) =>
          DateTime.fromISO(a.timestamp_start).toMillis() -
          DateTime.fromISO(b.timestamp_start).toMillis(),
      );
    setEntries(normalized);
  }, [open, building, typeKey]);

  const now = DateTime.now();

  const classify = (e) => {
    const s = DateTime.fromISO(e.timestamp_start);
    const ed = DateTime.fromISO(e.timestamp_end);
    if (s <= now && now <= ed) return 'active';
    if (now < s) return 'future';
    return 'past';
  };

  const validateDraft = () => {
    const { value, start, end } = draft;
    if (!value || !start || !end) {
      setError('Alle felter er påkrævet.');
      return false;
    }
    const s = DateTime.fromISO(start);
    const e = DateTime.fromISO(end);
    if (!s.isValid || !e.isValid) {
      setError('Ugyldig dato.');
      return false;
    }
    if (s >= e) {
      setError('Startdato skal være før slutdato.');
      return false;
    }
    const overlaps = entries.some((x) => {
      const xs = DateTime.fromISO(x.timestamp_start);
      const xe = DateTime.fromISO(x.timestamp_end);
      return s <= xe && e >= xs; // inclusive overlap
    });
    if (overlaps) {
      setError('Perioden overlapper en eksisterende værdi.');
      return false;
    }
    setError('');
    return true;
  };

  const addEntry = () => {
    if (!validateDraft()) return;
    const next = [
      ...entries,
      {
        timestamp_start: DateTime.fromISO(draft.start).toFormat(
          "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
        ),
        timestamp_end: DateTime.fromISO(draft.end).toFormat(
          "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
        ),
        value: Number(draft.value),
      },
    ].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp_start).toMillis() -
        DateTime.fromISO(b.timestamp_start).toMillis(),
    );
    setEntries(next);
    setDraft({ value: '', start: '', end: '' });
  };

  const removeEntry = (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    setEntries(next);
  };

  const handleClose = () => {
    if (isUpdating) return;
    onClose?.();
    setDraft({ value: '', start: '', end: '' });
    setError('');
  };

  const handleSave = async () => {
    if (!typeKey) return handleClose();

    // If user filled the draft but didn't press "+", include it on save
    const hasDraft = draft.value || draft.start || draft.end;
    let pending = entries;

    if (hasDraft) {
      if (!validateDraft()) return; // show error
      pending = [
        ...entries,
        {
          timestamp_start: DateTime.fromISO(draft.start).toFormat(
            "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
          ),
          timestamp_end: DateTime.fromISO(draft.end).toFormat(
            "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
          ),
          value: Number(draft.value),
        },
      ].sort(
        (a, b) =>
          DateTime.fromISO(a.timestamp_start).toMillis() -
          DateTime.fromISO(b.timestamp_start).toMillis(),
      );
    }

    const nextCoefficients = {
      ...(building?.UserInput?.CO2Coefficients || {}),
      [typeKey]: pending,
    };

    const updatedBuildingPayload = {
      Name: building.Name,
      Location: {
        Address: building.Location.Address,
        Latitude: building.Location.Latitude,
        Longitude: building.Location.Longitude,
      },
      BaselineDayInterval: building.BaselineDayInterval,
      BaselineDayOffset: building.BaselineDayOffset,
      UserInput: {
        ...(building.UserInput || {}),
        CO2Coefficients: nextCoefficients,
      },
    };

    const result = await updateBuilding({
      buildingId: building.BuildingId,
      building: updatedBuildingPayload,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'CO2 koefficienter opdateret',
          severity: 'success',
        }),
      );

      handleClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('co2eCoefficientDialog.title', { ns: 'buildingOptimization' })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          {t('co2eCoefficientDialog.description', {
            item: typeKey,
            ns: 'buildingOptimization',
          })}
        </Typography>

        <List sx={{ mb: 2 }}>
          {entries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              {t('co2eCoefficientDialog.noValueSet', {
                item: defaultCoefficient,
                ns: 'buildingOptimization',
              })}{' '}
              g/kWh
            </Typography>
          )}
          {entries.map((e, idx) => {
            const status = classify(e);
            const bg =
              status === 'active'
                ? (theme) => alpha(theme.palette.success.main, 0.1)
                : status === 'future'
                  ? (theme) => alpha(theme.palette.info.main, 0.04)
                  : undefined;
            return (
              <ListItem
                key={`${e.timestamp_start}-${idx}`}
                sx={{ borderRadius: 2, mb: 1, bgcolor: bg }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => removeEntry(idx)}
                    sx={{
                      borderRadius: 2,
                      mr: 1,
                    }}
                  >
                    <DeleteRoundedIcon
                      sx={{ ':hover': { color: 'error.main' } }}
                    />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">
                        {Number(e.value).toFixed(1)} g/kWh
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          status === 'active'
                            ? 'Aktiv'
                            : status === 'future'
                              ? 'Kommende'
                              : 'Tidligere'
                        }
                        color={
                          status === 'active'
                            ? 'success'
                            : status === 'future'
                              ? 'info'
                              : 'default'
                        }
                        sx={{
                          borderRadius: 2,
                          backgroundColor:
                            status === 'active'
                              ? (theme) =>
                                  alpha(theme.palette.success.main, 0.5)
                              : status === 'future'
                                ? (theme) =>
                                    alpha(theme.palette.info.main, 0.04)
                                : undefined,
                        }}
                        variant={status === 'active' ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {DateTime.fromISO(e.timestamp_start).toFormat(
                        'yyyy-LL-dd',
                      )}
                      {/*eslint-disable-next-line react/jsx-no-literals*/}
                      {' -> '}
                      {DateTime.fromISO(e.timestamp_end).toFormat('yyyy-LL-dd')}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('actions.addItem', {
            item: t('co2eCoefficientDialog.period', {
              ns: 'buildingOptimization',
            }),
          })}
        </Typography>
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: 'block' }}
          >
            {error}
          </Typography>
        )}
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size="grow">
            <TextField
              label="CO2e-koefficient"
              type="number"
              value={draft.value}
              onChange={(e) =>
                setDraft((d) => ({ ...d, value: e.target.value }))
              }
              variant="filled"
              fullWidth
              slotProps={{
                input: { endAdornment: <span>g/kWh</span> },
              }}
            />
          </Grid>
          <Grid size="grow">
            <TextField
              label="Start"
              type="date"
              value={draft.start}
              onChange={(e) =>
                setDraft((d) => ({ ...d, start: e.target.value }))
              }
              variant="filled"
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size="grow">
            <TextField
              label="Slut"
              type="date"
              value={draft.end}
              onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
              variant="filled"
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid>
            <Tooltip title={t('buildingOptimization:tooltips.addPeriod')}>
              <IconButton
                color="primary"
                onClick={addEntry}
                size="large"
                sx={{ mb: 0.5, borderRadius: 2 }}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <InfoRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {t('co2eCoefficientDialog.noOverlap', {
              ns: 'buildingOptimization',
            })}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('actions.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!typeKey || isUpdating}
        >
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
